import {useMutation, useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {Subscription} from '@common/billing/subscription';

/**
 * Client for the public trial-code endpoints. Every endpoint 404s while the
 * feature is switched off, so callers must treat a failed validate as
 * "no offer" rather than as an error to show.
 */

export type TrialRefusalReason =
  | 'invalid_code'
  | 'code_unavailable'
  | 'not_available_in_country'
  | 'already_subscribed'
  | 'already_used';

/**
 * What a visitor who is not signed in sees first: the offer with a sign-up
 * button, or the phone box (one SMS code creates the account and starts the
 * trial).
 */
export type TrialEntryFlow = 'offer' | 'phone';

export interface ValidateTrialCodeResponse extends BackendResponse {
  valid: boolean;
  code?: string;
  trial_days?: number;
  phone_verification_required?: boolean;
  // for the phone field: countries it may offer (empty = any), and where a
  // number typed without a country code is looked for
  countries?: string[];
  phone_regions?: string[];
  entry_flow?: TrialEntryFlow;
}

/**
 * Whether a failed validate call means "there is no offer": the endpoint 404s
 * while the feature is off. Anything else (network, 5xx, 429) is a failure to
 * load, not an answer.
 */
export function isTrialOfferGoneError(error: unknown): boolean {
  return (
    (error as {response?: {status?: number}})?.response?.status === 404
  );
}

interface ValidateTrialCodeOptions {
  // how long an answer is reused before asking again (default one minute)
  staleTime?: number;
}

export function useValidateTrialCode(
  code: string | null | undefined,
  options: ValidateTrialCodeOptions = {},
) {
  return useQuery({
    queryKey: ['billing/trial-codes/validate', code],
    queryFn: () =>
      apiClient
        .get<ValidateTrialCodeResponse>('billing/trial-codes/validate', {
          params: {code},
        })
        .then(r => r.data),
    enabled: !!code,
    // a 404 means the feature is off: that is an answer, not a failure to
    // retry. Anything else gets two more tries.
    retry: (failureCount, error) =>
      !isTrialOfferGoneError(error) && failureCount < 2,
    // the visitor leaves for the SMS app and comes back mid-activation; a
    // refetch then could only disturb the form
    refetchOnWindowFocus: false,
    staleTime: options.staleTime ?? 60_000,
  });
}

export interface SendTrialCodePayload {
  code: string;
  phone: string;
}

export function useSendTrialCode() {
  return useMutation({
    mutationFn: (payload: SendTrialCodePayload) =>
      apiClient
        .post<BackendResponse>('billing/trial-codes/send-code', payload)
        .then(r => r.data),
  });
}

export interface RedeemTrialCodePayload {
  code: string;
  phone?: string;
  otp_code?: string;
  tracking_parameters?: Record<string, string>;
}

export interface RedeemTrialCodeResponse extends BackendResponse {
  subscription: Subscription;
  trial_ends_at: string;
}

export function useRedeemTrialCode() {
  return useMutation({
    mutationFn: (payload: RedeemTrialCodePayload) =>
      apiClient
        .post<RedeemTrialCodeResponse>('billing/trial-codes/redeem', payload)
        .then(r => r.data),
  });
}

// The phone-first sign-up signs the visitor in, so it goes through the web
// routes ("secure/..." is not given the api/v1 prefix), like phone sign-up.
const SIGN_UP_START = 'secure/billing/trial-codes/sign-up/start';
const SIGN_UP_VERIFY = 'secure/billing/trial-codes/sign-up/verify';

export function useStartTrialSignUp() {
  return useMutation({
    mutationFn: (payload: SendTrialCodePayload) =>
      apiClient.post<BackendResponse>(SIGN_UP_START, payload).then(r => r.data),
  });
}

export interface TrialSignUpPayload {
  code: string;
  phone: string;
  otp_code: string;
  // kept on the trial: the campaign that started it
  tracking_parameters?: Record<string, string>;
  // kept on a new account: what a normal sign-up records
  account_tracking_parameters?: Record<string, string>;
}

export interface TrialSignUpResponse extends BackendResponse {
  // the signed-in state, as after a login
  bootstrapData: string;
  account_created: boolean;
  trial_started: boolean;
  trial_ends_at: string | null;
  // why no trial was started, when one was not
  reason: TrialRefusalReason | null;
}

export function useVerifyTrialSignUp() {
  return useMutation({
    mutationFn: (payload: TrialSignUpPayload) =>
      apiClient
        .post<TrialSignUpResponse>(SIGN_UP_VERIFY, payload)
        .then(r => r.data),
  });
}

/**
 * The machine-readable refusal reason from a failed send-code/redeem call, if
 * the server gave one.
 */
export function getTrialRefusalReason(
  error: unknown,
): TrialRefusalReason | undefined {
  const reason = (error as {response?: {data?: {reason?: unknown}}})?.response
    ?.data?.reason;
  return typeof reason === 'string'
    ? (reason as TrialRefusalReason)
    : undefined;
}

/**
 * The first validation message for a field, if the server returned one.
 */
export function getTrialFieldError(
  error: unknown,
  field: string,
): string | undefined {
  const errors = (error as {response?: {data?: {errors?: Record<string, unknown>}}})
    ?.response?.data?.errors;
  const value = errors?.[field];
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : undefined;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * The top-level error message from a failed call, if any.
 */
export function getTrialErrorMessage(error: unknown): string | undefined {
  const message = (error as {response?: {data?: {message?: unknown}}})?.response
    ?.data?.message;
  return typeof message === 'string' && message !== '' ? message : undefined;
}
